/* IONMAX Asthma Week 2026 — Healthy Home Check modal.
   Answers stay in the browser: nothing is submitted, stored or persisted. */
(() => {
  const init = (root) => {
    if (!root || root.dataset.awkReady === 'true') return;

    const dialog = root.querySelector('[data-awk-dialog]');
    const form = root.querySelector('[data-awk-form]');
    if (!dialog || !form || typeof dialog.showModal !== 'function') return;

    const openButtons = [...root.querySelectorAll('[data-awk-open]')];
    const steps = [...form.querySelectorAll('[data-awk-step]')];
    const resultStep = form.querySelector('[data-awk-result]');
    const closeButton = form.querySelector('[data-awk-close]');
    const backButton = form.querySelector('[data-awk-back]');
    const nextButton = form.querySelector('[data-awk-next]');
    const restartButton = form.querySelector('[data-awk-restart]');
    const footer = form.querySelector('[data-awk-footer]');
    const progressLabel = form.querySelector('[data-awk-progress-label]');
    const progressBar = form.querySelector('[data-awk-progress-bar]');
    const resultTitle = form.querySelector('[data-awk-result-title]');
    const resultCopy = form.querySelector('[data-awk-result-copy]');
    const resultLink = form.querySelector('[data-awk-result-link]');

    if (!steps.length || !resultStep || !nextButton || !resultLink) return;

    root.dataset.awkReady = 'true';

    let paths = {};
    try {
      paths = JSON.parse(root.dataset.awkPaths || '{}');
    } catch (error) {
      paths = {};
    }

    const nextLabel = nextButton.dataset.labelNext || 'Continue';
    const finishLabel = nextButton.dataset.labelFinish || 'See my starting point';
    const progressTemplate = progressLabel ? progressLabel.dataset.template || 'Question [current] of [total]' : '';
    const completeLabel = progressLabel ? progressLabel.dataset.complete || 'Check complete' : '';

    let current = 0;
    let opener = null;

    const selectedOnStep = () => steps[current] && steps[current].querySelector('input:checked');

    const render = () => {
      steps.forEach((step, index) => step.classList.toggle('is-active', index === current));
      resultStep.classList.remove('is-active');
      if (footer) footer.hidden = false;
      if (backButton) backButton.hidden = current === 0;
      nextButton.disabled = !selectedOnStep();
      nextButton.textContent = current === steps.length - 1 ? finishLabel : nextLabel;
      if (progressLabel) {
        progressLabel.textContent = progressTemplate
          .replace('[current]', String(current + 1))
          .replace('[total]', String(steps.length));
      }
      if (progressBar) progressBar.style.transform = `scaleX(${(current + 1) / steps.length})`;
      const heading = steps[current].querySelector('h3');
      if (heading) heading.focus({ preventScroll: true });
    };

    const resetCheck = () => {
      form.reset();
      current = 0;
      render();
    };

    const showResult = () => {
      const scores = { monitor: 0, purify: 0, moisture: 0 };
      form.querySelectorAll('input[type="radio"]:checked').forEach((input) => {
        scores.monitor += Number(input.dataset.monitor || 0);
        scores.purify += Number(input.dataset.purify || 0);
        scores.moisture += Number(input.dataset.moisture || 0);
      });

      const ranked = Object.entries(scores).sort((a, b) => b[1] - a[1]);
      const key = ranked[0][1] === 0 ? 'monitor' : ranked[0][0];
      const path = paths[key] || paths.monitor;
      if (!path) return;

      if (resultTitle) resultTitle.textContent = path.title;
      if (resultCopy) resultCopy.textContent = path.copy;
      resultLink.textContent = path.cta;
      resultLink.dataset.awkTarget = path.target;

      steps.forEach((step) => step.classList.remove('is-active'));
      resultStep.classList.add('is-active');
      if (progressLabel) progressLabel.textContent = completeLabel;
      if (progressBar) progressBar.style.transform = 'scaleX(1)';
      if (footer) footer.hidden = true;
      if (resultTitle) resultTitle.focus({ preventScroll: true });
    };

    const openCheck = (trigger) => {
      opener = trigger || null;
      dialog.showModal();
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      resetCheck();
    };

    dialog.addEventListener('close', () => {
      if (!dialog.open) {
        document.documentElement.style.overflow = '';
        document.body.style.overflow = '';
      }
      if (opener && document.contains(opener)) opener.focus({ preventScroll: true });
      opener = null;
    });

    // Close on Escape explicitly rather than relying on the UA close request.
    document.addEventListener('keydown', (event) => {
      if (!dialog.open) return;
      if (event.key !== 'Escape' && event.key !== 'Esc') return;
      event.preventDefault();
      dialog.close();
    });

    openButtons.forEach((button) => {
      button.addEventListener('click', () => openCheck(button));
    });

    if (closeButton) closeButton.addEventListener('click', () => dialog.close());
    if (restartButton) restartButton.addEventListener('click', resetCheck);

    if (backButton) {
      backButton.addEventListener('click', () => {
        if (current > 0) current -= 1;
        render();
      });
    }

    nextButton.addEventListener('click', () => {
      if (!selectedOnStep()) return;
      if (current < steps.length - 1) {
        current += 1;
        render();
      } else {
        showResult();
      }
    });

    form.addEventListener('change', (event) => {
      if (event.target.matches('input[type="radio"]')) nextButton.disabled = !selectedOnStep();
    });

    resultLink.addEventListener('click', (event) => {
      event.preventDefault();
      const target = resultLink.dataset.awkTarget ? root.querySelector(resultLink.dataset.awkTarget) : null;
      dialog.close();
      if (!target) return;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
      target.classList.add('is-highlighted');
      target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
      window.setTimeout(() => target.classList.remove('is-highlighted'), 2400);
    });

    // Backdrop close: only when the pointer is genuinely outside the dialog box.
    dialog.addEventListener('click', (event) => {
      if (event.target !== dialog) return;
      const bounds = dialog.getBoundingClientRect();
      const outside =
        event.clientX < bounds.left ||
        event.clientX > bounds.right ||
        event.clientY < bounds.top ||
        event.clientY > bounds.bottom;
      if (outside) dialog.close();
    });

    render();
  };

  const initAll = (scope = document) => {
    if (scope.matches?.('[data-ionmax-asthma-week]')) init(scope);
    scope.querySelectorAll?.('[data-ionmax-asthma-week]').forEach(init);
  };

  if (window.IonmaxAsthmaWeek?.initAll) {
    window.IonmaxAsthmaWeek.initAll();
    return;
  }

  window.IonmaxAsthmaWeek = { initAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
